// © Microsoft Corporation. All rights reserved.
import { DirectionalStyleSheetBehavior, display, focusVisible, forcedColorsStylesheetBehavior } from "@microsoft/fast-foundation";
import { accentFillActiveBehavior, accentFillHoverBehavior, elevation, neutralFillHoverBehavior, neutralFocusBehavior, neutralForegroundActiveBehavior, neutralForegroundHintBehavior, neutralForegroundRestBehavior, neutralLayerFloatingBehavior } from "@fluentui/web-components";
import { css } from "@microsoft/fast-element";
import { SystemColors } from "@microsoft/fast-web-utilities";

const ltr = css`
    li:first-child {
        padding-left: 0;
    }

    li:last-child,
    li.pipe {
        padding-right: 0;
    }

    .pipe::after {
        border-right: 1px solid #cecece;
        padding-left: 18px;
    }

    .overflowMenu {
        right: 0;
    }
`;

const rtl = css`
    .list {
        padding: 0 0 0 15px;
    }

    li:first-child {
        padding-right: 0;
    }

    li:last-child,
    li.pipe {
        padding-left: 0;
    }

    .pipe::after {
        border-left: 1px solid #cecece;
        padding-right: 18px;
    }

    .overflowMenu {
        left: 0;
    }
`;

export const navBarStyles = css`
    ${display("flex")} :host {
        align-items: center;
        color: ${neutralForegroundRestBehavior.var};
        fill: ${neutralForegroundRestBehavior.var};
    }

    .list {
        height: auto;
        overflow: hidden;
        list-style: none;
        padding: 0 15px 0 0;
        margin: 0;
        display: inline-flex;
        align-items: center;
    }

    li {
        list-style: none;
        padding: 0 16px;
        display: flex;
    }

    .hidden {
        visibility: hidden;
    }

    .overflowContainer {
        position: relative;
    }

    .overflowContainer fluent-button svg {
        vertical-align: middle;
    }

    .overflowContainer .hidden {
        display: none;
    }

    .overflowContainer ul li {
        padding: 5px 0;
        margin: 0 calc((10 + (var(--design-unit) * 2 * var(--density))) * 1px);
    }

    .endContainer {
        display: flex;
        margin-inline-start: auto;
        align-items: center;
    }

    .overflowMenu {
        position: absolute;
        --elevation: 11;
        background: ${neutralLayerFloatingBehavior.var};
        border: calc(var(--outline-width) * 1px) solid transparent;
        border-radius: var(--elevated-corner-radius);
        ${elevation}
        margin: 0;
        padding: 0;
        max-width: 368px;
        min-width: 100px;
        z-index: 3;
    }

    .pipe::after {
        content: "";
        height: 14px;
        align-self: center;
    }

    fluent-anchor {
        font-family: var(--body-font);
        outline: none;
        border-radius: calc(var(--corner-radius) * 1px);
        border: calc(var(--focus-outline-width) * 1px) solid transparent;
        font-family: var(--body-font);
        outline: none;
        border-radius: calc(var(--corner-radius) * 1px);
        border: calc(var(--focus-outline-width) * 1px) solid transparent;
    }

    .control {
        font-size: var(--type-ramp-base-font-size);
        line-height: var(--type-ramp-base-line-height);
        box-sizing: border-box;
        justify-content: center;
        align-items: center;
        white-space: nowrap;
        outline: none;
        text-decoration: none;
        cursor: pointer;
        color: ${neutralForegroundRestBehavior.var};
        fill: ${neutralForegroundRestBehavior.var};
        border-bottom: 2px solid transparent;
    }

    .active .control {
        border-bottom-color: ${accentFillActiveBehavior.var};
        color: ${neutralForegroundActiveBehavior.var};
        fill: ${neutralForegroundActiveBehavior.var};
        font-weight: 600;
    }

    .active .control:hover {
        border-bottom-color: ${accentFillHoverBehavior.var};
    }

    .active .control:active {
        border-bottom-color: ${accentFillActiveBehavior.var};
    }

    .control:hover {
        border-bottom-color: ${neutralFillHoverBehavior.var};
    }

    .control:active {
        border-bottom-color: ${neutralForegroundActiveBehavior.var};
    }

    fluent-anchor([active]:${focusVisible}),
    fluent-anchor(:${focusVisible}) {
        border-color: ${neutralFocusBehavior.var};
    }

    .control::-moz-focus-inner {
        border: 0;
    }

    fluent-anchor(.disabled) {
        opacity: var(--disabled-opacity);
    }

    fluent-anchor::slotted([slot="start"]) {
        vertical-align: middle;
    }    
    `
    .withBehaviors(
        neutralLayerFloatingBehavior,
        neutralForegroundHintBehavior,
        neutralForegroundRestBehavior,
        new DirectionalStyleSheetBehavior(ltr, rtl),
        forcedColorsStylesheetBehavior(
            css`
                .control {
                    border-bottom-color: ${SystemColors.ButtonFace};
                    color: ${SystemColors.LinkText};
                    fill: currentColor;
                }

                .active .control {
                    border-bottom-color: ${SystemColors.LinkText};
                    color: ${SystemColors.LinkText};
                    fill: currentColor;
                }
            `
        )
    );
